/**
 * Equipment Search Utility
 *
 * Navigates to the right view for a scanned text's backend lookup result:
 * - single   → equipment details (folder-aware)
 * - multiple → equipment list filtered by the scanned text
 * - none     → new-equipment or search, depending on the caller
 *
 * Used by:
 * - Scanner in the equipment-list toolbar
 * - Background NFC listener (CapacitorSetup)
 *
 * Matching is done server-side (lookupEquipment); this module only decides
 * where to navigate from the resulting EquipmentLookupResponse.
 */

import type { EquipmentLookupResponse } from '@geartracker/types';

import { paths } from 'src/routes/paths';

type Router = {
  push: (path: string) => void;
};

type NavigateToEquipmentOptions = {
  /** What to do when no equipment is found */
  onNoResult: 'new-equipment' | 'search';
  /** Current folder context (for folder-aware navigation) */
  folderId?: number;
};

/**
 * The shareable id a GearTracker NFC tag points at, or null if the URL isn't a
 * shortlink. Used to tell a tag that was physically tapped from any other way
 * of reaching the same page.
 */
export function shareableIdFromShortlinkUrl(url: string): string | null {
  try {
    const segments = new URL(url).pathname.split('/').filter(Boolean);
    const [root, shareableId, ...rest] = segments;

    return root === 'i' && shareableId && rest.length === 0
      ? shareableId
      : null;
  } catch {
    return null;
  }
}

/**
 * Every equipment a scan resolved to.
 *
 * A tag shared by several equipments means they travel together — a kit, a bag,
 * a vehicle load — so a scan locates all of them at once, not just the case
 * where the match is unambiguous.
 */
export function scannedEquipmentIds(lookup: EquipmentLookupResponse): number[] {
  switch (lookup.type) {
    case 'single':
      return [lookup.equipment.id];
    case 'multiple':
      return lookup.equipments.map((equipment) => equipment.id);
    case 'none':
      return [];
  }
}

/**
 * Navigate to the appropriate view for a scanned text's lookup result.
 *
 * - 1 result:         navigate to equipment details
 * - multiple results: navigate to the equipment list with a search filter
 * - no result:        navigate to new-equipment OR search (per options)
 *
 * @returns the lookup response, so callers can react (e.g. show a snackbar).
 */
export function searchAndNavigateToEquipment(
  searchText: string,
  lookup: EquipmentLookupResponse | undefined,
  router: Router,
  options: NavigateToEquipmentOptions,
): EquipmentLookupResponse {
  const result: EquipmentLookupResponse = lookup ?? { type: 'none' };

  switch (result.type) {
    case 'single': {
      const equipment = result.equipment;
      // In a folder context, drop the folder when the equipment lives elsewhere.
      if (options.folderId && equipment.folderId !== options.folderId) {
        router.push(paths.equipment.details(equipment.id, undefined));
      } else {
        router.push(paths.equipment.details(equipment.id, equipment.folderId));
      }
      break;
    }

    case 'multiple':
      router.push(
        paths.equipment.root(
          undefined,
          `search=${encodeURIComponent(searchText)}`,
        ),
      );
      break;

    case 'none':
      if (options.onNoResult === 'new-equipment') {
        router.push(paths.equipment.new());
      } else {
        router.push(
          paths.equipment.root(
            undefined,
            `search=${encodeURIComponent(searchText)}`,
          ),
        );
      }
      break;
  }

  return result;
}
